import React, { useState, useEffect, useRef } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { PlusIcon, PencilSquareIcon, TrashIcon, XMarkIcon } from '@heroicons/react/24/outline';
import PageBanner from '../../components/utils/PageBanner';
import GraphqlService from '../../service/graphqlService';
import MultiSelectDropdown from '../../components/MultiSelectDropdown';
import { useSnackbar } from '../../providers/SnackbarContext';
import usePermission from '../../authorization/permissionHook';
import useAnalytics from '../../hooks/useAnalytics';
import PolicyTemplatesHelpModal from './PolicyTemplatesHelpModal';
import PolicyTemplatesTutorial from './PolicyTemplatesTutorial';
import {
  GET_POLICY_TEMPLATE_DETAILS, CREATE_POLICY_TEMPLATE, UPDATE_POLICY_TEMPLATE, DELETE_POLICY_TEMPLATE,
  LIST_VARIABLES, CREATE_VARIABLE, UPDATE_VARIABLE, DELETE_VARIABLE,
} from '../../utils/constants';

const LoadingSpinner = () => (
  <div className="flex justify-center items-center h-32">
    <div className="animate-spin rounded-full h-8 w-8 border-t-2 border-b-2 border-blue-500" />
    <p className="ml-2 text-gray-700">Loading...</p>
  </div>
);

// A minimal, valid example policy to show as a placeholder / starting point.
const EXAMPLE_PERMISSION = JSON.stringify([
  {
    actions: ['iot:Publish', 'iot:Subscribe'],
    resources: [{ resource: 'arn:aws:iot:*:*:topic/example', service: 'iot' }],
  },
], null, 2);

// Feature 031 - validates the pasted JSON policy client-side before submit (FR-003).
// Returns { permission, error } - permission is the parsed array (camelCase, ready for the
// GraphQL mutation) when valid, or null with a human-readable error otherwise.
const parseAndValidatePermission = (rawJson) => {
  let parsed;
  try {
    parsed = JSON.parse(rawJson);
  } catch (e) {
    return { permission: null, error: 'Invalid JSON: please check the syntax.' };
  }
  if (!Array.isArray(parsed) || parsed.length === 0) {
    return { permission: null, error: 'Permission policy must be a non-empty JSON array of statements.' };
  }
  for (let i = 0; i < parsed.length; i++) {
    const statement = parsed[i];
    if (!Array.isArray(statement.actions) || statement.actions.length === 0) {
      return { permission: null, error: `Statement ${i + 1} must have a non-empty "actions" array.` };
    }
    if (!Array.isArray(statement.resources) || statement.resources.length === 0) {
      return { permission: null, error: `Statement ${i + 1} must have a non-empty "resources" array.` };
    }
    for (let r = 0; r < statement.resources.length; r++) {
      const resource = statement.resources[r];
      if (!resource.resource || !resource.service) {
        return { permission: null, error: `Statement ${i + 1}, resource ${r + 1} must have both "resource" and "service".` };
      }
    }
  }
  return { permission: parsed, error: null };
};

// Feature 031 (+027) - reusable inline change-request tracker shown after a create/update
// that returned change id(s), so the operator can check IAM propagation status on demand.
const ChangeRequestTracker = ({ changeIds }) => {
  const [statuses, setStatuses] = useState({});
  const [checkingId, setCheckingId] = useState(null);

  if (!changeIds || changeIds.length === 0) return null;

  const handleCheck = async (changeId) => {
    setCheckingId(changeId);
    try {
      const status = await GraphqlService.getChangeRequestStatus(changeId);
      setStatuses((prev) => ({ ...prev, [changeId]: status }));
    } catch (e) {
      // eslint-disable-next-line no-console
      console.error('Failed to check change request status', e);
    } finally {
      setCheckingId(null);
    }
  };

  return (
    <div className="mt-2 p-2 bg-blue-50 border border-blue-200 rounded text-xs text-blue-800 space-y-1">
      {changeIds.map((changeId) => (
        <div key={changeId} className="flex items-center justify-between gap-2">
          <span>
            Change request <span className="font-mono">{changeId}</span>
            {statuses[changeId] && `: ${statuses[changeId].status}`}
          </span>
          <button
            type="button"
            disabled={checkingId === changeId}
            onClick={() => handleCheck(changeId)}
            className="px-2 py-0.5 bg-blue-100 hover:bg-blue-200 text-blue-800 rounded disabled:opacity-50 shrink-0"
          >
            {checkingId === changeId ? 'Checking…' : 'Check status'}
          </button>
        </div>
      ))}
    </div>
  );
};

// ============================================================================
// Templates tab
// ============================================================================

const TemplateFormModal = ({
  isOpen, onClose, templateId, canWrite, serviceOptions, canCreateVariable,
}) => {
  const openSnackBar = useSnackbar();
  const queryClient = useQueryClient();
  const isEditing = !!templateId;

  const { data: existingTemplate, isLoading } = useQuery(
    ['policyTemplateDetails', templateId],
    () => GraphqlService.getPolicyTemplateDetails(templateId),
    { enabled: isOpen && isEditing, refetchOnWindowFocus: false },
  );

  const [description, setDescription] = useState('');
  const [services, setServices] = useState([]);
  const [readOnly, setReadOnly] = useState(false);
  const [permissionJson, setPermissionJson] = useState(EXAMPLE_PERMISSION);
  const [jsonError, setJsonError] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [changeIds, setChangeIds] = useState([]);
  const [showInlineVariableModal, setShowInlineVariableModal] = useState(false);
  const permissionTextareaRef = useRef(null);

  // Feature 031 polish - variables referenced by {{NAME}} in the permission JSON are managed
  // on the separate Variables tab; surface the known ones here too (and a shortcut to create
  // a new one) so the connection between the two isn't hidden behind a tab switch.
  const { data: availableVariables = [] } = useQuery(
    ['variables'],
    () => GraphqlService.listVariables(),
    { enabled: isOpen, retry: false },
  );

  const insertVariablePlaceholder = (name) => {
    const placeholder = `{{${name}}}`;
    const el = permissionTextareaRef.current;
    if (!el) {
      setPermissionJson((prev) => `${prev}${placeholder}`);
      return;
    }
    const start = el.selectionStart ?? permissionJson.length;
    const end = el.selectionEnd ?? permissionJson.length;
    setPermissionJson(`${permissionJson.slice(0, start)}${placeholder}${permissionJson.slice(end)}`);
    requestAnimationFrame(() => {
      el.focus();
      const pos = start + placeholder.length;
      el.setSelectionRange(pos, pos);
    });
  };

  // Pre-fill the form once the existing template's details have loaded.
  useEffect(() => {
    if (isEditing && existingTemplate) {
      setDescription(existingTemplate.description || '');
      setServices(existingTemplate.services || []);
      setReadOnly(!!existingTemplate.readOnly);
      setPermissionJson(JSON.stringify(existingTemplate.permission || [], null, 2));
    }
  }, [isEditing, existingTemplate]);

  const resetForm = () => {
    setDescription('');
    setServices([]);
    setReadOnly(false);
    setPermissionJson(EXAMPLE_PERMISSION);
    setJsonError('');
    setChangeIds([]);
    setShowInlineVariableModal(false);
  };

  const handleClose = () => {
    resetForm();
    onClose();
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!description.trim()) {
      openSnackBar('Description is required.', 'error');
      return;
    }
    const servicesList = services;
    if (servicesList.length === 0) {
      openSnackBar('At least one service is required.', 'error');
      return;
    }
    const { permission, error } = parseAndValidatePermission(permissionJson);
    if (error) {
      setJsonError(error);
      return;
    }
    setJsonError('');
    setIsSubmitting(true);
    try {
      if (isEditing) {
        const { changeIds: newChangeIds } = await GraphqlService.updatePolicyTemplate(templateId, description.trim(), servicesList, readOnly, permission);
        openSnackBar('Template updated successfully!', 'success');
        setChangeIds(newChangeIds);
      } else {
        await GraphqlService.createPolicyTemplate(description.trim(), servicesList, readOnly, permission);
        openSnackBar('Template created successfully!', 'success');
        handleClose();
      }
      queryClient.invalidateQueries(['policyTemplates']);
      queryClient.invalidateQueries(['policyTemplateDetails', templateId]);
    } catch (err) {
      openSnackBar(err.message || 'Failed to save template.', 'error');
    } finally {
      setIsSubmitting(false);
    }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex justify-center items-center p-4 z-50">
      <div className="bg-white rounded-lg shadow-xl w-full max-w-2xl max-h-[90vh] flex flex-col">
        <div className="flex justify-between items-center p-4 border-b border-gray-200">
          <h3 className="text-xl font-semibold text-gray-800">{isEditing ? 'Edit Template' : 'Create Template'}</h3>
          <button onClick={handleClose} className="text-gray-400 hover:text-gray-600" aria-label="Close">
            <XMarkIcon className="h-6 w-6" />
          </button>
        </div>
        {isEditing && isLoading ? <LoadingSpinner /> : (
          <form onSubmit={handleSubmit} className="overflow-y-auto p-6 space-y-4">
            <div>
              <label htmlFor="description" className="block text-sm font-medium text-gray-700 mb-1">Description *</label>
              <input
                id="description"
                type="text"
                value={description}
                onChange={(e) => setDescription(e.target.value)}
                disabled={!canWrite}
                className="w-full p-2 border border-gray-300 rounded-md shadow-sm focus:ring-blue-500 focus:border-blue-500"
                placeholder="e.g., Read-only access to Data Dictionary"
              />
            </div>
            <div data-tutorial="template-services">
              <label htmlFor="services" className="block text-sm font-medium text-gray-700 mb-1">Services *</label>
              {canWrite ? (
                <MultiSelectDropdown
                  options={serviceOptions}
                  selectedValues={services}
                  onChange={setServices}
                  placeholder="Select services"
                />
              ) : (
                <div className="p-2 border border-gray-300 rounded-md shadow-sm bg-gray-100 sm:text-sm min-h-[40px]">
                  {services.map((service) => (
                    <span key={service} className="inline-block bg-blue-100 text-blue-800 rounded-full px-2 py-1 text-xs font-semibold mr-2 mb-1">{service}</span>
                  ))}
                </div>
              )}
            </div>
            <div className="flex items-center">
              <input
                id="readOnly"
                type="checkbox"
                checked={readOnly}
                onChange={(e) => setReadOnly(e.target.checked)}
                disabled={!canWrite}
                className="mr-2 rounded border-gray-300 text-indigo-600 focus:ring-indigo-500"
              />
              <label htmlFor="readOnly" className="text-sm text-gray-700">Read-only access</label>
            </div>
            <div data-tutorial="template-permission-json">
              <div className="flex items-center justify-between mb-1">
                <label htmlFor="permissionJson" className="block text-sm font-medium text-gray-700">Permission Policy (JSON) *</label>
                {canWrite && canCreateVariable && (
                  <button
                    type="button"
                    data-tutorial="template-new-variable"
                    onClick={() => setShowInlineVariableModal(true)}
                    className="text-xs text-indigo-600 hover:text-indigo-800 font-medium shrink-0"
                  >
                    + New Variable
                  </button>
                )}
              </div>
              <textarea
                id="permissionJson"
                ref={permissionTextareaRef}
                rows="10"
                value={permissionJson}
                onChange={(e) => { setPermissionJson(e.target.value); setJsonError(''); }}
                disabled={!canWrite}
                className={`w-full p-2 border ${jsonError ? 'border-red-500' : 'border-gray-300'} rounded-md shadow-sm font-mono text-xs focus:ring-blue-500 focus:border-blue-500`}
              />
              {jsonError && <p className="text-red-500 text-xs mt-1">{jsonError}</p>}
              <p className="mt-1 text-xs text-gray-500">Array of statements, each with &quot;actions&quot; (list of action strings) and &quot;resources&quot; (list of {'{ resource, service }'}).</p>
              {canWrite && availableVariables.length > 0 && (
                <div className="mt-2">
                  <p className="text-xs text-gray-500 mb-1">Insert a variable as a {'{{placeholder}}'} at the cursor:</p>
                  <div className="flex flex-wrap gap-1">
                    {availableVariables.map((variable) => (
                      <button
                        key={variable.name}
                        type="button"
                        onClick={() => insertVariablePlaceholder(variable.name)}
                        title={`Insert {{${variable.name}}}`}
                        className="text-xs font-mono bg-indigo-50 hover:bg-indigo-100 text-indigo-700 rounded px-2 py-0.5"
                      >
                        {`{{${variable.name}}}`}
                      </button>
                    ))}
                  </div>
                </div>
              )}
            </div>
            <ChangeRequestTracker changeIds={changeIds} />
            <div className="flex justify-end gap-2 pt-2">
              <button type="button" onClick={handleClose} className="px-4 py-2 bg-gray-100 hover:bg-gray-200 text-gray-700 rounded-md">
                {changeIds.length > 0 ? 'Close' : 'Cancel'}
              </button>
              {canWrite && (
                <button type="submit" disabled={isSubmitting} className="px-4 py-2 bg-indigo-600 hover:bg-indigo-700 text-white rounded-md disabled:opacity-50">
                  {isSubmitting ? 'Saving…' : (isEditing ? 'Update' : 'Create')}
                </button>
              )}
            </div>
          </form>
        )}
      </div>
      <VariableFormModal
        isOpen={showInlineVariableModal}
        onClose={() => setShowInlineVariableModal(false)}
        variable={null}
        canWrite={canCreateVariable}
      />
    </div>
  );
};

const DeleteTemplateModal = ({ isOpen, onClose, template }) => {
  const openSnackBar = useSnackbar();
  const queryClient = useQueryClient();
  const [isDeleting, setIsDeleting] = useState(false);

  if (!isOpen || !template) return null;

  const handleDelete = async () => {
    setIsDeleting(true);
    try {
      await GraphqlService.deletePolicyTemplate(template.templateId);
      openSnackBar('Template deleted successfully!', 'success');
      queryClient.invalidateQueries(['policyTemplates']);
      onClose();
    } catch (err) {
      openSnackBar(err.message || 'Failed to delete template. It may still be in use.', 'error');
    } finally {
      setIsDeleting(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex justify-center items-center p-4 z-50">
      <div className="bg-white rounded-lg shadow-xl w-full max-w-md p-6">
        <h3 className="text-lg font-semibold text-gray-800 mb-2">Delete Template</h3>
        <p className="text-sm text-gray-600 mb-4">
          Are you sure you want to delete <strong>{template.description}</strong>? This cannot be undone. If this template is currently assigned to any credential, deletion will be blocked.
        </p>
        <div className="flex justify-end gap-2">
          <button onClick={onClose} className="px-4 py-2 bg-gray-100 hover:bg-gray-200 text-gray-700 rounded-md">Cancel</button>
          <button onClick={handleDelete} disabled={isDeleting} className="px-4 py-2 bg-red-600 hover:bg-red-700 text-white rounded-md disabled:opacity-50">
            {isDeleting ? 'Deleting…' : 'Delete'}
          </button>
        </div>
      </div>
    </div>
  );
};

const TemplatesTab = ({
  canCreate, canUpdate, canDelete, canCreateVariable, tutorialForceCreate,
}) => {
  const [showFormModal, setShowFormModal] = useState(false);
  const [editingTemplateId, setEditingTemplateId] = useState(null);
  const [templateToDelete, setTemplateToDelete] = useState(null);
  const [tutorialOpenedForm, setTutorialOpenedForm] = useState(false);

  const { data: templates = [], isLoading } = useQuery(
    ['policyTemplates'],
    () => GraphqlService.getTemplates(),
    { refetchOnWindowFocus: false },
  );

  // Feature 031 tutorial - open the Create Template form on demand so the walkthrough can
  // point at real Services/Permission JSON fields instead of just describing them.
  useEffect(() => {
    if (tutorialForceCreate) {
      setEditingTemplateId(null);
      setShowFormModal(true);
      setTutorialOpenedForm(true);
    } else if (tutorialOpenedForm) {
      setShowFormModal(false);
      setTutorialOpenedForm(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tutorialForceCreate]);

  // Services are always drawn from the platform's known set - derive the options from every
  // service already used across existing templates, rather than free-text entry (which risks
  // typos that would silently create an unusable/duplicate service name).
  const serviceOptions = React.useMemo(() => {
    const uniqueServices = new Set();
    templates.forEach((template) => (template.services || []).forEach((service) => uniqueServices.add(service)));
    return Array.from(uniqueServices).sort().map((service) => ({ value: service, label: service }));
  }, [templates]);

  const handleCreate = () => {
    setEditingTemplateId(null);
    setShowFormModal(true);
  };

  const handleEdit = (templateId) => {
    setEditingTemplateId(templateId);
    setShowFormModal(true);
  };

  return (
    <div>
      <div className="flex justify-between items-center mb-4">
        <p className="text-sm text-gray-600">
          Permission templates define which platform APIs/services a credential can access — pick one or more when
          {' '}adding a new credential or editing an existing one&apos;s Permissions in Credentials Management.
          Retires the manual Postman procedure (PROC036).
        </p>
        {canCreate && (
          <button
            onClick={handleCreate}
            data-tutorial="template-create"
            className="bg-blue-500 hover:bg-blue-700 text-white font-bold py-2 px-4 rounded flex items-center shrink-0 ml-4"
          >
            <PlusIcon className="h-5 w-5 mr-2" />
            Create Template
          </button>
        )}
      </div>

      {isLoading ? <LoadingSpinner /> : (
        <div className="bg-white shadow-md rounded-lg overflow-hidden">
          <div className="hidden md:flex bg-blue-600 text-white items-center">
            <div className="p-4 w-4/12 font-semibold">Description</div>
            <div className="p-4 w-5/12 font-semibold">Services</div>
            <div className="p-4 w-2/12 font-semibold text-center">Template ID</div>
            <div className="p-4 w-1/12 font-semibold text-center">Actions</div>
          </div>
          {templates.length === 0 ? (
            <div className="p-6 text-center text-gray-500">No templates found.</div>
          ) : templates.map((template, index) => (
            <div key={template.templateId} className="flex flex-col md:flex-row border-b border-gray-200 hover:bg-gray-50 items-start">
              <div className="p-4 w-4/12 break-words">{template.description}</div>
              <div className="p-4 w-5/12">
                {(template.services || []).map((service) => (
                  <span key={service} className="inline-block bg-blue-100 text-blue-800 rounded-full px-2 py-1 text-xs font-semibold mr-2 mb-1">
                    {service}
                  </span>
                ))}
              </div>
              <div className="p-4 w-2/12 text-center text-xs font-mono text-gray-500 truncate" title={template.templateId}>{template.templateId}</div>
              <div className="p-4 w-1/12 flex justify-center space-x-2">
                <button
                  onClick={() => handleEdit(template.templateId)}
                  title={canUpdate ? 'View & Edit Template' : 'View Template (Read-only)'}
                  data-tutorial={index === 0 ? 'template-edit' : undefined}
                  className="text-gray-500 hover:text-blue-600"
                >
                  <PencilSquareIcon className="h-5 w-5" />
                </button>
                {canDelete && (
                  <button
                    onClick={() => setTemplateToDelete(template)}
                    title="Delete Template"
                    data-tutorial={index === 0 ? 'template-delete' : undefined}
                    className="text-red-500 hover:text-red-700"
                  >
                    <TrashIcon className="h-5 w-5" />
                  </button>
                )}
              </div>
            </div>
          ))}
        </div>
      )}

      <TemplateFormModal
        isOpen={showFormModal}
        onClose={() => setShowFormModal(false)}
        templateId={editingTemplateId}
        canWrite={editingTemplateId ? canUpdate : canCreate}
        serviceOptions={serviceOptions}
        canCreateVariable={canCreateVariable}
      />
      <DeleteTemplateModal
        isOpen={!!templateToDelete}
        onClose={() => setTemplateToDelete(null)}
        template={templateToDelete}
      />
    </div>
  );
};

// ============================================================================
// Variables tab
// ============================================================================

const VariableFormModal = ({ isOpen, onClose, variable, canWrite }) => {
  const openSnackBar = useSnackbar();
  const queryClient = useQueryClient();
  const isEditing = !!variable;
  const [name, setName] = useState(variable?.name || '');
  const [value, setValue] = useState(variable?.value || '');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [changeIds, setChangeIds] = useState([]);

  if (!isOpen) return null;

  const handleClose = () => {
    setName('');
    setValue('');
    setChangeIds([]);
    onClose();
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!name.trim() || !value.trim()) {
      openSnackBar('Name and value are required.', 'error');
      return;
    }
    setIsSubmitting(true);
    try {
      if (isEditing) {
        const { changeIds: newChangeIds } = await GraphqlService.updateVariable(name.trim(), value.trim());
        openSnackBar('Variable updated successfully!', 'success');
        setChangeIds(newChangeIds);
      } else {
        await GraphqlService.createVariable(name.trim(), value.trim());
        openSnackBar('Variable created successfully!', 'success');
        handleClose();
      }
      queryClient.invalidateQueries(['variables']);
    } catch (err) {
      openSnackBar(err.message || 'Failed to save variable.', 'error');
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex justify-center items-center p-4 z-50">
      <div className="bg-white rounded-lg shadow-xl w-full max-w-md p-6">
        <div className="flex justify-between items-center mb-4">
          <h3 className="text-xl font-semibold text-gray-800">{isEditing ? 'Edit Variable' : 'Create Variable'}</h3>
          <button onClick={handleClose} className="text-gray-400 hover:text-gray-600" aria-label="Close">
            <XMarkIcon className="h-6 w-6" />
          </button>
        </div>
        <form onSubmit={handleSubmit} className="space-y-4">
          <div data-tutorial="variable-name">
            <label htmlFor="varName" className="block text-sm font-medium text-gray-700 mb-1">Name *</label>
            <input
              id="varName"
              type="text"
              value={name}
              onChange={(e) => setName(e.target.value)}
              disabled={isEditing || !canWrite}
              className="w-full p-2 border border-gray-300 rounded-md shadow-sm bg-white disabled:bg-gray-100 focus:ring-blue-500 focus:border-blue-500"
              placeholder="e.g., DEFAULT_REGION"
            />
          </div>
          <div>
            <label htmlFor="varValue" className="block text-sm font-medium text-gray-700 mb-1">Value * (used for {'{{VARIABLE}}'} substitutions in templates)</label>
            <input
              id="varValue"
              type="text"
              value={value}
              onChange={(e) => setValue(e.target.value)}
              disabled={!canWrite}
              className="w-full p-2 border border-gray-300 rounded-md shadow-sm focus:ring-blue-500 focus:border-blue-500"
            />
          </div>
          <ChangeRequestTracker changeIds={changeIds} />
          <div className="flex justify-end gap-2 pt-2">
            <button type="button" onClick={handleClose} className="px-4 py-2 bg-gray-100 hover:bg-gray-200 text-gray-700 rounded-md">
              {changeIds.length > 0 ? 'Close' : 'Cancel'}
            </button>
            {canWrite && (
              <button type="submit" disabled={isSubmitting} className="px-4 py-2 bg-indigo-600 hover:bg-indigo-700 text-white rounded-md disabled:opacity-50">
                {isSubmitting ? 'Saving…' : (isEditing ? 'Update' : 'Create')}
              </button>
            )}
          </div>
        </form>
      </div>
    </div>
  );
};

const DeleteVariableModal = ({ isOpen, onClose, variable }) => {
  const openSnackBar = useSnackbar();
  const queryClient = useQueryClient();
  const [isDeleting, setIsDeleting] = useState(false);

  if (!isOpen || !variable) return null;

  const handleDelete = async () => {
    setIsDeleting(true);
    try {
      await GraphqlService.deleteVariable(variable.name);
      openSnackBar('Variable deleted successfully!', 'success');
      queryClient.invalidateQueries(['variables']);
      onClose();
    } catch (err) {
      openSnackBar(err.message || 'Failed to delete variable.', 'error');
    } finally {
      setIsDeleting(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex justify-center items-center p-4 z-50">
      <div className="bg-white rounded-lg shadow-xl w-full max-w-md p-6">
        <h3 className="text-lg font-semibold text-gray-800 mb-2">Delete Variable</h3>
        <p className="text-sm text-gray-600 mb-4">
          Are you sure you want to delete <strong>{variable.name}</strong>? This cannot be undone.
        </p>
        <div className="flex justify-end gap-2">
          <button onClick={onClose} className="px-4 py-2 bg-gray-100 hover:bg-gray-200 text-gray-700 rounded-md">Cancel</button>
          <button onClick={handleDelete} disabled={isDeleting} className="px-4 py-2 bg-red-600 hover:bg-red-700 text-white rounded-md disabled:opacity-50">
            {isDeleting ? 'Deleting…' : 'Delete'}
          </button>
        </div>
      </div>
    </div>
  );
};

const VariablesTab = ({
  canCreate, canUpdate, canDelete, tutorialForceCreate,
}) => {
  const [showFormModal, setShowFormModal] = useState(false);
  const [editingVariable, setEditingVariable] = useState(null);
  const [variableToDelete, setVariableToDelete] = useState(null);
  const [tutorialOpenedForm, setTutorialOpenedForm] = useState(false);

  const { data: variables = [], isLoading, isError, error } = useQuery(
    ['variables'],
    () => GraphqlService.listVariables(),
    { refetchOnWindowFocus: false, retry: false },
  );

  // Feature 031 tutorial - open the Create Variable form on demand, mirroring TemplatesTab.
  useEffect(() => {
    if (tutorialForceCreate) {
      setEditingVariable(null);
      setShowFormModal(true);
      setTutorialOpenedForm(true);
    } else if (tutorialOpenedForm) {
      setShowFormModal(false);
      setTutorialOpenedForm(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tutorialForceCreate]);

  return (
    <div>
      <div className="flex justify-between items-center mb-4">
        <p className="text-sm text-gray-600">
          Reusable values you can insert into a template&apos;s Permission Policy JSON as {'{{NAME}}'} placeholders
          {' '}(e.g. so several templates can share one account ID without repeating it). You can also create one
          {' '}directly from a template&apos;s form via its <strong>+ New Variable</strong> button.
        </p>
        {canCreate && (
          <button
            onClick={() => { setEditingVariable(null); setShowFormModal(true); }}
            data-tutorial="variable-create"
            className="bg-blue-500 hover:bg-blue-700 text-white font-bold py-2 px-4 rounded flex items-center shrink-0 ml-4"
          >
            <PlusIcon className="h-5 w-5 mr-2" />
            Create Variable
          </button>
        )}
      </div>

      {isError && (
        <div className="mb-4 p-3 bg-amber-50 border border-amber-300 rounded-md text-sm text-amber-800">
          <strong>Variables are temporarily unavailable:</strong> {error?.message || 'the platform rejected this request.'} This is a known platform/infra-side access gap (not a Portal bug) - the Portal&apos;s backend role isn&apos;t yet authorized to call the Variables API. Ask the Platform/Infra team to grant it.
        </div>
      )}

      {isLoading ? <LoadingSpinner /> : (
        <div className="bg-white shadow-md rounded-lg overflow-hidden">
          <div className="hidden md:flex bg-blue-600 text-white items-center">
            <div className="p-4 w-3/12 font-semibold">Name</div>
            <div className="p-4 w-4/12 font-semibold">Value</div>
            <div className="p-4 w-2/12 font-semibold">Created By</div>
            <div className="p-4 w-2/12 font-semibold">Updated On</div>
            <div className="p-4 w-1/12 font-semibold text-center">Actions</div>
          </div>
          {variables.length === 0 ? (
            <div className="p-6 text-center text-gray-500">{isError ? 'Unable to load variables.' : 'No variables found.'}</div>
          ) : variables.map((variable, index) => (
            <div key={variable.name} className="flex flex-col md:flex-row border-b border-gray-200 hover:bg-gray-50 items-start">
              <div className="p-4 w-3/12 font-mono text-sm break-words">{variable.name}</div>
              <div className="p-4 w-4/12 break-words">{variable.value}</div>
              <div className="p-4 w-2/12 text-sm text-gray-600 break-words">{variable.createdBy || 'N/A'}</div>
              <div className="p-4 w-2/12 text-sm text-gray-600">{variable.updatedOn ? new Date(variable.updatedOn).toLocaleDateString() : 'N/A'}</div>
              <div className="p-4 w-1/12 flex justify-center space-x-2">
                {canUpdate && (
                  <button onClick={() => { setEditingVariable(variable); setShowFormModal(true); }} title="Edit Variable" data-tutorial={index === 0 ? 'variable-edit' : undefined} className="text-gray-500 hover:text-blue-600">
                    <PencilSquareIcon className="h-5 w-5" />
                  </button>
                )}
                {canDelete && (
                  <button onClick={() => setVariableToDelete(variable)} title="Delete Variable" data-tutorial={index === 0 ? 'variable-delete' : undefined} className="text-red-500 hover:text-red-700">
                    <TrashIcon className="h-5 w-5" />
                  </button>
                )}
              </div>
            </div>
          ))}
        </div>
      )}

      <VariableFormModal
        isOpen={showFormModal}
        onClose={() => setShowFormModal(false)}
        variable={editingVariable}
        canWrite={editingVariable ? canUpdate : canCreate}
      />
      <DeleteVariableModal
        isOpen={!!variableToDelete}
        onClose={() => setVariableToDelete(null)}
        variable={variableToDelete}
      />
    </div>
  );
};

// ============================================================================
// Main screen
// ============================================================================

export default function PolicyTemplatesManagement() {
  const [activeTab, setActiveTab] = useState('templates');
  const [showHelpModal, setShowHelpModal] = useState(false);
  const [showTutorial, setShowTutorial] = useState(false);
  const [tutorialForceCreate, setTutorialForceCreate] = useState(false);
  const { setAnalytics } = useAnalytics();

  useEffect(() => {
    setAnalytics({ event: 'section_loaded', sectionId: 'policy_templates_management' });
  }, [setAnalytics]);

  const [, canCreateTemplate] = usePermission(CREATE_POLICY_TEMPLATE);
  const [, canUpdateTemplate] = usePermission(UPDATE_POLICY_TEMPLATE);
  const [, canDeleteTemplate] = usePermission(DELETE_POLICY_TEMPLATE);
  const [, canReadTemplate] = usePermission(GET_POLICY_TEMPLATE_DETAILS);
  const [, canListVariables] = usePermission(LIST_VARIABLES);
  const [, canCreateVariable] = usePermission(CREATE_VARIABLE);
  const [, canUpdateVariable] = usePermission(UPDATE_VARIABLE);
  const [, canDeleteVariable] = usePermission(DELETE_VARIABLE);

  return (
    <div className="bg-gray-100 min-h-screen">
      <PageBanner title="POLICY TEMPLATES MANAGEMENT" subtitle="Create and manage permission templates and substitution variables used by credentials." />
      <div className="p-6">
        <div className="mb-4 border-b border-gray-200 flex justify-between items-center">
          <nav className="flex space-x-4">
            <button
              onClick={() => setActiveTab('templates')}
              className={`px-3 py-2 text-sm font-medium border-b-2 ${activeTab === 'templates' ? 'border-indigo-600 text-indigo-600' : 'border-transparent text-gray-500 hover:text-gray-700'}`}
            >
              Templates
            </button>
            {canListVariables && (
              <button
                onClick={() => setActiveTab('variables')}
                className={`px-3 py-2 text-sm font-medium border-b-2 ${activeTab === 'variables' ? 'border-indigo-600 text-indigo-600' : 'border-transparent text-gray-500 hover:text-gray-700'}`}
              >
                Variables
              </button>
            )}
          </nav>
          <button
            onClick={() => setShowTutorial(true)}
            className="bg-gray-100 hover:bg-gray-200 text-gray-700 w-9 h-9 rounded-full flex items-center justify-center shadow-md font-bold shrink-0"
            title="Help - Guided Tutorial"
            aria-label="Help"
          >
            ?
          </button>
        </div>

        {activeTab === 'templates' && (
          <TemplatesTab
            canCreate={canCreateTemplate}
            canUpdate={canUpdateTemplate || canReadTemplate}
            canDelete={canDeleteTemplate}
            canCreateVariable={canCreateVariable}
            tutorialForceCreate={tutorialForceCreate}
          />
        )}
        {activeTab === 'variables' && canListVariables && (
          <VariablesTab
            canCreate={canCreateVariable}
            canUpdate={canUpdateVariable}
            canDelete={canDeleteVariable}
            tutorialForceCreate={tutorialForceCreate}
          />
        )}
      </div>

      {showHelpModal && (
        <PolicyTemplatesHelpModal
          isOpen={showHelpModal}
          onClose={() => setShowHelpModal(false)}
          onStartTutorial={() => {
            setShowHelpModal(false);
            setShowTutorial(true);
          }}
        />
      )}

      {showTutorial && (
        <PolicyTemplatesTutorial
          isOpen={showTutorial}
          onClose={() => {
            setShowTutorial(false);
            setTutorialForceCreate(false);
          }}
          canListVariables={canListVariables}
          onRequestTab={setActiveTab}
          onRequestFormModal={setTutorialForceCreate}
          onShowWrittenGuide={() => {
            setShowTutorial(false);
            setTutorialForceCreate(false);
            setShowHelpModal(true);
          }}
        />
      )}
    </div>
  );
}
